<script setup>
import StoreFrontDefaultLayout from "@/Layouts/StoreFrontDefaultLayout.vue"
import CBreadcrumb from "@/Components/CBreadcrumb.vue"
import {computed, nextTick, ref, watch} from 'vue'
import {VNumberInput} from 'vuetify/labs/VNumberInput'
import CLink from "@/Components/CLink.vue"
import {Head, router, useForm, usePage} from "@inertiajs/vue3"
import {useDisplay} from "vuetify"
import {helpers} from "@/Services/helpers.js"
import CRelatedProducts from "@/Components/CRelatedProducts.vue"
import PlateForm from "@/Pages/ProductsTemplates/PlateForm.vue"
import {eventBus} from "@/Services/eventBus.js"
import CImageModal from "@/Components/CImageModal.vue"
import CImagesCarousel from "@/Components/CImagesCarousel.vue"
import PartnerStoreFrontDefaultLayout from "@/Layouts/PartnerStoreFrontDefaultLayout.vue"

const props = defineProps({
    breadcrumbs: Array,
    productDetail: Object,
})

const productDetail = props.productDetail
const product = productDetail.product
const variations = product.variations

const options = portalOptions
const hoveredMediaUrl = ref(null)
const selectedQuantities = ref(Array(product.variations.length).fill().map((_, index) => options.productMinAmount ? product.variations[index].item_profile.quant_min : 1))
const variantsQuantity = ref([])
const submitCustomizableTemplateForm = ref(false)
const mainMediaRef = ref(null)
const thumbRefs = ref([])
const descriptionExpanded = ref(false)
const descriptionSheetRef = ref(false)
const thumbnailsMediaSliderHeight = ref(300)
const thumbnailsMediaMaxHeight = ref(100)
const mainMediaMaxHeight = ref(600)
const descriptionHeight = ref(0)
const plateFormRef = ref(null)
const sizeGridImageModal = ref(false)
const productImagesCarouselModal = ref(false)

const page = usePage()
const display = useDisplay()
const canAddToCart = computed(() => {
    // exibimos o botão de adicionar ao carrinho se apenas uma variação possivel
    // ou se não tiver opção de escolha de cor ou se uma cor esta selecionada
    return variations.length === 1 || product.colors.length <= 1 || route().params.colorAttribute
})

const productReportForm = useForm({
    comment: null,
    productVariationId: null
})

const selectedVariation = ref(productDetail?.selectedAttributes?.length > 0 ? variations[0] : [])

watch(() => productDetail?.selectedAttributes ?? [], () => {
    selectedVariation.value = productDetail?.selectedAttributes?.length > 0 ? variations[0] : null
})

// primeira media do produto (favorita) -> fallback para primeira media da primeira variação
const mainMediaUrl = product.media_url || variations[0]?.media_url || '/assets/img/no-photo.png'

const addItemCart = async function () {
    const items = await getVariantsQuantity()

    router.post(route('cart.add'), {items}, {
        preserveState: false,
        preserveScroll: true,
        onSuccess: () => {
            eventBus.emit('openCart')
            helpers.toast('O produto foi adicionado ao carrinho com sucesso!')
            resetVariantsQuantity()
        }
    })
}

const getGradientStyle = function (color) {
    return `background: linear-gradient(127deg, ${color.hex} 50%, ${color.hexSec} 50%);`
}

const getVariantsQuantity = async function () {
    variantsQuantity.value = product.variations.map((variation, index) => ({
            id: variation.id,
            quantity: selectedQuantities.value[index] || 0
        })
    )

    if (plateFormRef.value) {
        submitCustomizableTemplateForm.value = true
    }

    return variantsQuantity.value
}

const resetVariantsQuantity = function () {
    product.variations.map((variation, index) => {
        selectedQuantities.value[index] = variation.item_profile.quant_min
    })
}

/** Calcula o tamanho da área de exibição da descrição para corrigir a exibição do resumo se necessário */
const updateDescriptionSheetHeight = function () {
    nextTick(() => {
        descriptionHeight.value = descriptionSheetRef.value.$el.scrollHeight
    })
}

/** Calcula o tamanho da imagem principal para aplicar ao slider de thumbs das variações */
const updateThumbnailsImageSlider = function () {
    nextTick(() => {
        thumbnailsMediaSliderHeight.value = mainMediaRef.value.$el.clientHeight
        mainMediaMaxHeight.value = mainMediaRef.value.$el.clientWidth
        thumbnailsMediaMaxHeight.value = thumbRefs.value[0]?.$el.clientWidth || 100
    })
}

const setThumbRef = (key) => (el) => thumbRefs.value[key] = el

const stockInfo = function (providerType, stock) {
    return providerType === 'local'
        ? stock.local
        : stock.fornecedor
}


const openReportDialog = ref(false)
const handleFormSubmit = async function () {
    try {
        if (productDetail.selectedAttributes?.length > 0) {
            productReportForm.productVariationId = variations[0].id
        }

        productReportForm.post(route('product.process-review', {product: product.id}), {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                helpers.toast('Revisão enviada com sucesso!')
                openReportDialog.value = false
                productReportForm.reset()
            }
        })
    } catch (error) {
        console.error('Erro ao processar formulário:', error)
        helpers.toast('Ocorreu um erro inesperado', 'error')
    }
}

const showMessage = ref(false)

const productReference = function () {
    return productDetail.selectedAttributes ? variations[0].reference : product.reference
}

const canShowLocalStock = function (variation) {
    return productDetail.canViewStock && variation.stock && variation.stock.local
}

const canShowSupplierStock = function (variation) {
    return productDetail.canViewStock && variation.stock && variation.stock.fornecedor
}

watch(descriptionSheetRef, () => {
    // Quando carregar a descriptionSheet calcular e atualizar o tamanho dela
    updateDescriptionSheetHeight()
})

watch(display.width, () => {
    // Quando a largura da página atualizar, re-calculamos o que for necessário
    updateDescriptionSheetHeight()
    updateThumbnailsImageSlider()
})

defineOptions({
    layout: portalOptions?.mainPortal ? StoreFrontDefaultLayout : PartnerStoreFrontDefaultLayout
})
</script>

<template>
    <Head :title="product.item_profile.product_title + (route().params?.colorAttribute ? ' | ' + route().params?.colorAttribute : '')"/>
    <v-container>
        <c-breadcrumb :items="breadcrumbs"/>
        <v-row class="mb-5">
            <v-col cols="12" md="6">
                <v-row>
                    <v-col cols="8" md="8">
                        <v-img
                            :src="hoveredMediaUrl || (productDetail.selectedAttributes?.length === 1 && variations[0] ? (variations[0]?.media_url || '/assets/img/no-photo.png') : mainMediaUrl)"
                            eager aspect-ratio="1/1" style="min-height: 400px;" v-bind:style="{'max-height': mainMediaMaxHeight+'px'}" ref="mainMediaRef"
                            @load="updateThumbnailsImageSlider"/>
                    </v-col>
                    <v-col cols="4" md="4">
                        <v-slide-group direction="vertical" prev-icon="mdi-chevron-up" next-icon="mdi-chevron-down"
                                       v-bind:style="{height: thumbnailsMediaSliderHeight+'px'}">
                            <v-slide-group-item v-for="(media, key) in productDetail.medias">
                                <v-img :src="media.url" class="cursor-pointer" aspect-ratio="1/1" :style="{'max-height': thumbnailsMediaMaxHeight+'px'}" eager
                                       @click="productImagesCarouselModal = true" @mouseover="hoveredMediaUrl=media.url" @mouseleave="hoveredMediaUrl = null"
                                       :ref="setThumbRef(key)"/>
                            </v-slide-group-item>
                        </v-slide-group>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="6">
                <v-sheet class="pb-4">
                    <v-row>
                        <v-col md="12">
                            <p class="title">{{ product.item_profile.product_title }}
                                <v-btn v-if="page.props.auth.permissions.includes('update portal product variations')" rounded size="36" class="float-end mt-1"
                                       @click="router.get(route('admin.portal.products', {portal: productDetail.portalId, search: product.reference }))">
                                    <v-icon icon="mdi-pencil" size="20" color="grey-darken-2"/>
                                </v-btn>
                                <v-btn v-if="page.props.auth.permissions.includes('report product error')" rounded size="36"
                                       @click="openReportDialog = true" class="float-end me-2 mt-1">
                                    <v-icon icon="mdi-alert-circle" size="20" color="grey-darken-2"/>
                                </v-btn>
                            </p>
                        </v-col>
                    </v-row>
                    <p class="text-subtitle-1">Código do produto: {{ productReference() }}</p>
                </v-sheet>
                <v-sheet class="overflow-hidden text-justify faded-end" v-bind:class="{'expanded': descriptionExpanded}"
                         @click="descriptionExpanded = true" ref="descriptionSheetRef" :style="{
                        height: descriptionExpanded ? descriptionHeight + 'px' : '45px',
                        transition: 'height 0.3s ease',
                        whiteSpace: 'pre-line'
                    }">
                    {{ product.item_profile.description }}
                </v-sheet>
                <v-sheet class="text-center" style="height: 30px" v-if="descriptionHeight > 45">
                    <v-btn :icon="descriptionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="descriptionExpanded = !descriptionExpanded"
                           variant="flat" :color="descriptionExpanded ? 'grey-lighten-2' : 'transparent'" size="30"
                           :style="{'margin-top': descriptionExpanded ? '0' : '-10px'}"/>
                </v-sheet>
                <v-row class="pb-4">
                    <v-col md="7" v-if="product.colors.length > 1">
                        <p class="font-weight-bold pb-3">Cores Disponíveis:</p>
                        <v-sheet class="d-flex flex-wrap pl-2">
                            <c-link v-for="color in product.colors" :key="color.title" :href="color.variationUrl" :preserve-state="false">
                                <v-btn
                                    :style="getGradientStyle(color)"
                                    class="pa-0 mr-2 mt-2" min-width="35px" height="35px" :title="color.title" variant="flat"
                                    rounded
                                    v-bind:style="{
                                        'border': color.title?.toLowerCase() === route().params?.colorAttribute ? 'solid 1px #141312 !important' : ''
                                       }"
                                    v-bind:class="{
                                        'border-lg': color.title?.toLowerCase() === route().params?.colorAttribute,
                                        'elevation-2': color.title?.toLowerCase() !== route().params?.colorAttribute
                                       }"/>
                            </c-link>
                        </v-sheet>
                    </v-col>
                    <v-col md="5"
                           v-if="product.item_profile.width > 0 || product.item_profile.height > 0 || product.item_profile.length > 0 || product.item_profile.size_grid_img">
                        <p class="font-weight-bold pb-3">Dimensões:</p>
                        <v-sheet class="text-medium-emphasis pl-5">
                            <template v-if="product.item_profile.width > 0"><span class="font-weight-bold">Largura:</span> {{ product.item_profile.width }}
                                mm<br/></template>
                            <template v-if="product.item_profile.height > 0"><span class="font-weight-bold">Altura:</span> {{ product.item_profile.height }}
                                mm<br/></template>
                            <template v-if="product.item_profile.length > 0"><span class="font-weight-bold">Profundidade:</span> {{
                                    product.item_profile.length
                                }} mm<br/></template>
                            <template v-if="product.item_profile.weight > 0"><span class="font-weight-bold">Peso:</span> {{ product.item_profile.weight }}
                                kg<br/></template>
                            <template v-if="product.item_profile.diameter > 0"><span class="font-weight-bold">Diâmetro:</span> {{
                                    product.item_profile.diameter
                                }} mm<br/></template>
                            <template v-if="product.item_profile.size_grid_img"><span @click="sizeGridImageModal = true"
                                                                                      class="font-weight-bold cursor-pointer text-blue-accent-2">Ver tabela de tamanhos</span>
                                <br/></template>
                        </v-sheet>
                    </v-col>
                </v-row>
                <v-sheet>
                    <v-sheet v-if="canAddToCart">
                        <v-row :md="$vuetify.display.mobile ? 6 : 3" v-for="(variation, index) in variations" :key="variation.reference">
                            <v-col md="8" v-if="variations.length > 0" class="pl-6">
                                <v-row>
                                    <span v-if="variation.color" class="text-h6">{{ variation.color.toUpperCase() }}&nbsp;</span>
                                    <span v-if="variation.size" class="text-h6">&nbsp;- {{ variation.size }}</span>
                                    <span class="text-h6"
                                          v-if="variation.item_profile.min_price !== undefined || variation.item_profile.max_price !== undefined">
                                        <template v-if="variation.item_profile.min_price !== undefined">&nbsp;- R$ {{
                                                helpers.formatPrice(variation.item_profile.min_price)
                                            }}<template v-if="variation.item_profile.min_price !== variation.item_profile.max_price"> - {{
                                                    helpers.formatPrice(variation.item_profile.max_price)
                                                }}</template>
                                        }}</template>
                                        <template v-else> - R$ {{
                                                helpers.formatPrice(variation.item_profile.max_price)
                                            }}</template>
                                    </span>
                                </v-row>
                                <v-row>
                                    <template v-if="canShowLocalStock(variation)">
                                        <p>Estoque Local: {{ stockInfo('local', variation.stock) }}</p>
                                        <p></p>
                                    </template>
                                </v-row>
                                <v-row>
                                    <template v-if="canShowSupplierStock(variation)">
                                        <p>Estoque Fornecedor: {{ stockInfo('fornecedor', variation.stock) }}</p>
                                    </template>
                                </v-row>
                            </v-col>
                            <v-col :md="variations.length > 0 ? 4 : 12">
                                <v-number-input v-model="selectedQuantities[index]"
                                                :min="options.productMinAmount ? variation.item_profile.quant_min : 0"
                                                density="compact"
                                                variant="outlined" hide-details="auto"/>
                            </v-col>
                        </v-row>
                        <v-row class="justify-end pr-3" v-if="options.productMinAmount">
                            <span class="font-weight-bold text-grey-darken-3">Quantidade Minima: {{
                                    selectedVariation?.item_profile?.quant_min || variations[0].item_profile?.quant_min
                                }}</span>
                        </v-row>

                        <v-sheet class="pt-5" height="20vh" width="40vw"
                                 v-if="(selectedVariation?.item_profile?.customizable === 1) ||
                                (variations[0]?.item_profile.customizable === 1) &&
                                page.props.auth.user">
                            <span class="text-h6 font-weight-bold">Informaçoes Adicionais</span>
                            <plate-form ref="plateFormRef"
                                        v-if="selectedVariation?.item_profile?.customizable_template === 'plate' || variations[0]?.item_profile?.customizable_template === 'plate'"
                                        :variant="selectedVariation"
                                        v-model:variants="variantsQuantity" v-model:submitForm="submitCustomizableTemplateForm"/>
                        </v-sheet>
                    </v-sheet>
                    <v-row class="pt-5">
                        <v-spacer md="5"/>
                        <v-col class="text-right" md="7">
                            <v-btn @click="canAddToCart ? addItemCart() : showMessage = true" variant="flat" color="primary">
                                <span class="font-weight-bold">ADICIONAR AO ORÇAMENTO</span>
                            </v-btn>
                            <v-messages class="mt-2" style="font-size: 0.9em; font-weight: bold"
                                        :active="!canAddToCart && showMessage" color="red"
                                        messages="Selecione uma cor primeiro"
                            />
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-col>
        </v-row>
        <v-row class="py-10">
            <v-col md="12">
                <h2><span class="text-grey-darken-4 font-weight-bold">Produtos Relacionados</span></h2>
            </v-col>
            <v-col md="12">
                <c-related-products :products="productDetail.relatedProducts"/>
            </v-col>
        </v-row>
    </v-container>
    <v-dialog v-model="openReportDialog" max-width="500px">
        <v-card>
            <v-card-title class="pa-6 py-4 font-weight-bold">Reportar Erro no Produto</v-card-title>

            <v-card-text class="pa-6 py-4">
                <v-form @submit.prevent="handleFormSubmit">
                    <v-textarea
                        v-model="productReportForm.comment"
                        label="Descreva o problema encontrado"
                        :error-messages="productReportForm.errors.comment"
                    ></v-textarea>
                </v-form>
            </v-card-text>

            <v-card-actions class="pa-6 py-4">
                <v-spacer></v-spacer>
                <v-btn color="secondary" variant="flat" @click="openReportDialog = false">Cancelar</v-btn>
                <v-btn color="primary" variant="flat" type="submit" @click="handleFormSubmit" :loading="productReportForm.processing"
                >Enviar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="sizeGridImageModal" max-width="80%">
        <c-image-modal v-model="sizeGridImageModal" :img-url="product.item_profile.size_grid_img"></c-image-modal>
    </v-dialog>

    <v-dialog v-model="productImagesCarouselModal" max-width="80%">
        <c-images-carousel v-model="productImagesCarouselModal" :hovered-image-url="hoveredMediaUrl" :images-object="productDetail.medias"></c-images-carousel>
    </v-dialog>

</template>

<style scoped>
.title {
    font-size: 2em;
    font-weight: 700;
}

.faded-end {
    position: relative;
}

.faded-end::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 20px;
    background: linear-gradient(to bottom, transparent, white);
    transition: opacity 0.3s ease;
}

.faded-end.expanded::after {
    display: none;
}
</style>
